import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Nullable } from 'types/global.types';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { GooglePlacesCacheKeys } from 'types/cacheKeys.types';

// ---------------------------------------------------------------------------
// Google Places API (New) REST response types
// https://developers.google.com/maps/documentation/places/web-service/reference/rest/v1/places/autocomplete
// ---------------------------------------------------------------------------

type FormattableText = {
  text: string;
};

type PlacePrediction = {
  placeId: string;
  text: FormattableText;
  structuredFormat: {
    mainText: FormattableText;
    secondaryText?: FormattableText;
  };
};

type AutocompleteSuggestion = {
  placePrediction?: PlacePrediction;
};

type AutocompleteResponse = {
  suggestions?: AutocompleteSuggestion[];
};

type AddressComponent = {
  longText: string;
  shortText: string;
  types: string[];
};

type PlaceDetailsResponse = {
  addressComponents?: AddressComponent[];
};

// ---------------------------------------------------------------------------
// Hook public types
// ---------------------------------------------------------------------------

export type AddressResult = {
  street: string;
  city: string;
  zip: string;
  country: string;
  state?: string;
};

export type PlaceSuggestion = {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText?: string;
};

export type UseGoogleAddressAutocompleteOptions = {
  onPlaceSelect?: (address: AddressResult) => void;
  countryRestrictions?: string[];
  /**
   * Optional location context prepended to the search query to bias results
   * toward a known area. Pass all available context (zip, city, state) as an
   * array — falsy values are filtered out automatically. The most specific
   * signal (zip) disambiguates when multiple places share the same city name.
   *
   * @example ["80331", "München", "Bayern", undefined]
   */
  locationHint?: Array<Nullable<string>>;
};

export type UseGoogleAddressAutocompleteReturn = {
  suggestions: PlaceSuggestion[];
  isOpen: boolean;
  activeIndex: number;
  handleInputChange: (value: string) => void;
  handleSuggestionSelect: (suggestion: PlaceSuggestion) => Promise<void>;
  handleKeyDown: (e: KeyboardEvent) => void;
  closeDropdown: () => void;
};

// ---------------------------------------------------------------------------
// Constants & fetch helper
// ---------------------------------------------------------------------------

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places';
const SUGGESTIONS_STALE_TIME = 5 * 60 * 1000;

/**
 * Standalone fetch function used as the react-query `queryFn`.
 * Returns a promise with a `.cancel()` method so react-query v3
 * can abort in-flight requests on key change or unmount.
 */
const fetchSuggestions = (
  searchInput: string,
  countryRestrictions: string[] | undefined,
  apiKey: string,
  languageCode: string,
): Promise<PlaceSuggestion[]> & { cancel?: () => void } => {
  const controller = new AbortController();

  const body: Record<string, unknown> = {
    input: searchInput,
    languageCode,
  };
  if (countryRestrictions?.length) {
    body.includedRegionCodes = countryRestrictions;
  }

  const promise = fetch(AUTOCOMPLETE_URL, {
    method: 'POST',
    signal: controller.signal,
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok)
        throw new Error(`Places autocomplete failed: ${response.status}`);
      return response.json() as Promise<AutocompleteResponse>;
    })
    .then((data) =>
      (data.suggestions ?? [])
        .filter(
          (s): s is Required<AutocompleteSuggestion> => !!s.placePrediction,
        )
        .map((s) => ({
          placeId: s.placePrediction.placeId,
          description: s.placePrediction.text.text,
          mainText: s.placePrediction.structuredFormat.mainText.text,
          secondaryText: s.placePrediction.structuredFormat.secondaryText?.text,
        })),
    ) as Promise<PlaceSuggestion[]> & { cancel?: () => void };

  // react-query v3 cancellation pattern — the library calls promise.cancel()
  // when the query key changes or the component unmounts.
  // TODO: [BANK-3959] After migrating to @tanstack/react-query v5, remove
  //   this .cancel() workaround — v5 passes an AbortSignal via queryFn context
  //   automatically: queryFn: ({ signal }) => fetch(url, { signal })
  promise.cancel = () => controller.abort();

  return promise;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useGoogleAddressAutocomplete = ({
  onPlaceSelect,
  countryRestrictions,
  locationHint,
}: UseGoogleAddressAutocompleteOptions): UseGoogleAddressAutocompleteReturn => {
  const { i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const onPlaceSelectRef = useRef(onPlaceSelect);
  onPlaceSelectRef.current = onPlaceSelect;
  const languageRef = useRef(i18n.language);
  languageRef.current = i18n.language;

  const apiKey = import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY as
    | string
    | undefined;

  // -- Debounce the raw input -----------------------------------------------

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setDebouncedValue('');
      return;
    }
    const timer = setTimeout(() => setDebouncedValue(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // -- Build the search query -----------------------------------------------

  const hint = locationHint?.filter(Boolean).join(', ') || '';
  const searchInput = debouncedValue
    ? hint
      ? `${hint} ${debouncedValue}`
      : debouncedValue
    : '';

  // -- Autocomplete query ---------------------------------------------------
  // TODO: [BANK-3959] After migrating to @tanstack/react-query v5:
  //   - Use object syntax: useQuery({ queryKey, queryFn, enabled, staleTime })
  //   - Replace `isLoading` (if used) with `isPending`
  //   - The .cancel() workaround in fetchSuggestions can be removed (see above)

  const { data: suggestions = [] } = useQuery<PlaceSuggestion[]>(
    [
      GooglePlacesCacheKeys.Autocomplete,
      searchInput,
      countryRestrictions ?? [],
    ],
    () => {
      if (!apiKey || !searchInput) return [];
      return fetchSuggestions(
        searchInput,
        countryRestrictions,
        apiKey,
        languageRef.current,
      );
    },
    {
      enabled: searchInput.length >= 2 && !!apiKey,
      staleTime: SUGGESTIONS_STALE_TIME,
      retry: false,
    },
  );

  // -- Handlers -------------------------------------------------------------

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    if (!value || value.length < 2) {
      setIsOpen(false);
      setActiveIndex(-1);
    } else {
      setIsOpen(true);
      setActiveIndex(-1);
    }
  }, []);

  const handleSuggestionSelect = useCallback(
    async (suggestion: PlaceSuggestion) => {
      setIsOpen(false);
      setActiveIndex(-1);
      setInputValue('');
      setDebouncedValue('');

      if (!apiKey) return;

      try {
        const response = await fetch(
          `${PLACE_DETAILS_URL}/${suggestion.placeId}?languageCode=${languageRef.current}`,
          {
            headers: {
              'X-Goog-Api-Key': apiKey,
              'X-Goog-FieldMask': 'addressComponents',
            },
          },
        );

        if (!response.ok) {
          console.error('Place details request failed', response.status);
          return;
        }

        const place: PlaceDetailsResponse = await response.json();
        const components = place.addressComponents ?? [];

        let streetNumber = '';
        let route = '';
        let city = '';
        let zip = '';
        let country = '';
        let state = '';

        for (const c of components) {
          if (c.types.includes('street_number')) streetNumber = c.longText;
          if (c.types.includes('route')) route = c.longText;
          if (c.types.includes('locality')) city = c.longText;
          if (c.types.includes('postal_code')) zip = c.longText;
          if (c.types.includes('country')) country = c.shortText;
          if (c.types.includes('administrative_area_level_1'))
            state = c.longText;
        }

        const street = route
          ? streetNumber
            ? `${route} ${streetNumber}`
            : route
          : '';

        onPlaceSelectRef.current?.({ street, city, zip, country, state });
      } catch (err) {
        console.error('Failed to fetch place details', err);
      }
    },
    [apiKey],
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1,
          );
          break;
        case 'Enter':
          if (activeIndex >= 0 && activeIndex < suggestions.length) {
            e.preventDefault();
            void handleSuggestionSelect(suggestions[activeIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
      }
    },
    [isOpen, suggestions, activeIndex, handleSuggestionSelect, closeDropdown],
  );

  return {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleSuggestionSelect,
    handleKeyDown,
    closeDropdown,
  };
};
